import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { FormikProps, withFormik } from 'formik';
import { Button, Form, Icon } from 'semantic-ui-react';
import styled from 'styled-components';

import * as constants from '@/constants';
import { Admin, adminFactory } from '@/types/admin';
import { adminSchema } from '@/services/validators/admin';
import { evaluatePasswordStrength } from '@/utils/evaluatePasswordStrength';
import { classifiedSort } from '@/utils/name';
import { PasswordField } from '@/components/shared/PasswordField';
import { PasswordPolicy } from '@/components/shared/PasswordPolicy';
import { Label } from '@/components/shared/Label';

const strengthMeter = evaluatePasswordStrength(constants.PASSWORD_PATTERN);

type OuterProps = {
  adminId: number;
  token?: string;
  isDisable: boolean;
  onSubmit: (admin: Admin, password: string, token: string) => void;
};

type Values = {
  firstName: string;
  lastName: string;
  kanaFirstName: string;
  kanaLastName: string;
  password: string;
};

export const Activation = withFormik<OuterProps, Values>({
  mapPropsToValues: () => ({
    firstName: '',
    lastName: '',
    kanaFirstName: '',
    kanaLastName: '',
    password: '',
  }),
  validationSchema: adminSchema,
  handleSubmit: (values, { props, setSubmitting }) => {
    const admin = adminFactory({
      id: props.adminId,
      firstName: values.firstName,
      lastName: values.lastName,
      kanaFirstName: values.kanaFirstName,
      kanaLastName: values.kanaLastName,
    });
    props.onSubmit(admin, values.password, props.token || '');
    setSubmitting(false);
  },
})(
  // enhanced form
  ({
    errors,
    handleChange,
    handleSubmit,
    dirty,
    isSubmitting,
    isDisable = false,
    touched,
    values,
  }: OuterProps & FormikProps<Values>) => {
    const { t } = useTranslation();
    return (
      <Styled>
        <Form onSubmit={handleSubmit}>
          <div className="name-input">
            <label>
              {t('models.contact.personInCharge')}
              <Form.Group
                widths="equal"
                label={t('models.contact.personInCharge')}
              >
                {classifiedSort([
                  <Form.Field key="first-name">
                    <Form.Input
                      data-test="first-name"
                      className={classnames({
                        error: errors.firstName && touched.firstName,
                      })}
                      fluid={true}
                      key="first-name"
                      placeholder={t('ui.expression.firstName')}
                      name="firstName"
                      onChange={handleChange}
                      value={values.firstName}
                    />
                    {touched.firstName && errors.firstName && (
                      <Label classNames={'parallel-elem'}>
                        {errors.firstName}
                      </Label>
                    )}
                  </Form.Field>,
                  <Form.Field key="last-name">
                    <Form.Input
                      data-test="last-name"
                      className={classnames({
                        error: errors.lastName && touched.lastName,
                      })}
                      fluid={true}
                      key="last-name"
                      placeholder={t('ui.expression.lastName')}
                      name="lastName"
                      onChange={handleChange}
                      value={values.lastName}
                    />
                    {touched.lastName && errors.lastName && (
                      <Label classNames={'parallel-elem'}>
                        {errors.lastName}
                      </Label>
                    )}
                  </Form.Field>,
                ])}
              </Form.Group>
            </label>
          </div>
          <div className="hurikana-name">
            <label>
              {t('models.contact.personInCharge')}（
              {t('ui.expression.phonetic')}）
              <Form.Group widths="equal">
                {classifiedSort([
                  <Form.Field key="kana-first-name">
                    <Form.Input
                      data-test="kana-first-name"
                      className={classnames({
                        error: errors.kanaFirstName && touched.kanaFirstName,
                      })}
                      fluid={true}
                      placeholder={t('ui.expression.firstNamePhonetic')}
                      name="kanaFirstName"
                      onChange={handleChange}
                      value={values.kanaFirstName}
                    />
                    {touched.kanaFirstName && errors.kanaFirstName && (
                      <Label classNames={'parallel-elem'}>
                        {errors.kanaFirstName}
                      </Label>
                    )}
                  </Form.Field>,
                  <Form.Field key="kana-last-name">
                    <Form.Input
                      data-test="kana-last-name"
                      className={classnames({
                        error: errors.kanaLastName && touched.kanaLastName,
                      })}
                      fluid={true}
                      placeholder={t('ui.expression.lastNamePhonetic')}
                      name="kanaLastName"
                      onChange={handleChange}
                      value={values.kanaLastName}
                    />
                    {touched.kanaLastName && errors.kanaLastName && (
                      <Label classNames={'parallel-elem'}>
                        {errors.kanaLastName}
                      </Label>
                    )}
                  </Form.Field>,
                ])}
              </Form.Group>
            </label>
          </div>
          <PasswordField
            autoComplete="new-password"
            id={'password'}
            label={t('ui.password.label')}
            placeholder={t('ui.password.placeholder')}
            withToggle={true}
            onChange={handleChange}
            strengthMeter={strengthMeter}
            value={values.password}
          />
          {touched.password && errors.password && (
            <div>
              <Label>{errors.password}</Label>
            </div>
          )}
          <PasswordPolicy />
          <div className="submit-button">
            <div className="admin-activation-terms">
              <a
                href="https://digital.globis.co.jp/term/1"
                rel="noopener noreferrer"
                target="_blank"
              >
                {t('ui.terms.glp')}
                &nbsp;&nbsp;
                <Icon name="external" />
              </a>
            </div>
            <Button
              data-test="do-activation"
              disabled={
                !dirty ||
                isSubmitting ||
                isDisable ||
                strengthMeter(values.password) < 2
              }
              primary={true}
              size="large"
              type="submit"
            >
              {t('ui.action.withAgreement.activate')}
            </Button>
          </div>
        </Form>
      </Styled>
    );
  },
);

const Styled = styled.div`
  .address-form .button-group {
    margin: 2em 0 3em;
  }

  .hurikana-name {
    margin: 0 0 2.2em;
  }

  .admin-activation-terms {
    margin-bottom: 2em;
  }
`;
